'use client';

import { useCallback, useEffect, useState } from 'react';

import { useCustomerSession } from '@/lib/customer-bridge';
import {
  canonicalAllergenLabels,
  DIETARY_TAG_ENTRIES,
  resolveAllergenKey,
  resolveDietaryTagKey,
} from '@/lib/menu/allergen-vocabulary';

/**
 * Doc 40 §5 , customer allergen & dietary guard.
 *
 * The guard is intentionally client-side and synchronous so it protects guests
 * (no login) exactly like signed-in diners, works offline, and sends no dietary
 * data to any server. The device-local profile in `localStorage` is the source
 * of truth for the guard; a signed-in customer may additionally persist it to
 * their account for cross-device restore (wired with the Preferences work).
 *
 * Honesty note baked into the model: we only ever *warn/block* on an item's
 * explicit `allergens` list ("contains peanuts"). Dietary flags (vegan, halal…)
 * are positive markers , absence of a tag is not proof, so they never raise a
 * false "this isn't vegan" alarm here; they're used only as a positive filter.
 */

export type DietaryStrictness = 'warn' | 'block';

export interface DietaryProfile {
  /** Positive diet markers the customer follows (vegetarian, vegan, halal…). */
  dietaryFlags: string[];
  /** Allergens the customer avoids , the warn/block signal. */
  allergenAvoids: string[];
  strictness: DietaryStrictness;
}

export const EMPTY_DIETARY_PROFILE: DietaryProfile = {
  dietaryFlags: [],
  allergenAvoids: [],
  strictness: 'warn',
};

/** The colloquial allergen tokens offered in the customer profile editor. Each
 *  token maps to a canonical allergen via resolveAllergenKey() (lib/menu/
 *  allergen-vocabulary.ts), so a profile saying "dairy" guards items whose
 *  merchant-declared list says "Milk". This is a curated subset of the
 *  vocabulary's aliases, deliberately kept small for the customer sheet. */
export const COMMON_ALLERGENS: readonly string[] = [
  'peanuts',
  'tree nuts',
  'milk',
  'eggs',
  'gluten',
  'wheat',
  'soy',
  'shellfish',
  'fish',
  'sesame',
];

/** Positive dietary markers offered in the editor. */
export const COMMON_DIETARY_FLAGS: readonly string[] = [
  'vegetarian',
  'vegan',
  'halal',
  'kosher',
  'gluten-free',
  'dairy-free',
];

const STORAGE_KEY = 'mnrq:dietary-profile';
const UPDATE_EVENT = 'mnrq:dietary-profile-updated';

function normalizeToken(value: string): string {
  return value.trim().toLowerCase();
}

/** Loose overlap: exact match, or one token contained in the other (nut ↔ tree nuts). */
function tokenMatches(itemToken: string, avoidToken: string): boolean {
  const a = normalizeToken(itemToken);
  const b = normalizeToken(avoidToken);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

function sanitizeProfile(raw: unknown): DietaryProfile {
  if (!raw || typeof raw !== 'object') return EMPTY_DIETARY_PROFILE;
  const record = raw as Partial<DietaryProfile>;
  const list = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string').map(normalizeToken) : [];
  return {
    dietaryFlags: list(record.dietaryFlags),
    allergenAvoids: list(record.allergenAvoids),
    strictness: record.strictness === 'block' ? 'block' : 'warn',
  };
}

export function readDietaryProfile(): DietaryProfile {
  if (typeof window === 'undefined') return EMPTY_DIETARY_PROFILE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_DIETARY_PROFILE;
    return sanitizeProfile(JSON.parse(raw));
  } catch {
    return EMPTY_DIETARY_PROFILE;
  }
}

export function writeDietaryProfile(profile: DietaryProfile): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitizeProfile(profile)));
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT));
  } catch {
    // Ignore storage failures (private mode, quota).
  }
}

export function isDietaryProfileEmpty(profile: DietaryProfile): boolean {
  return profile.dietaryFlags.length === 0 && profile.allergenAvoids.length === 0;
}

export type DietaryStatus = 'clear' | 'warn' | 'blocked';

export interface DietaryEvaluation {
  status: DietaryStatus;
  /** Item allergens that collide with the customer's avoid list. */
  allergenHits: string[];
}

/**
 * Pure matcher , no I/O, safe to call on every render. `blocked` only when the
 * customer chose strict mode; otherwise a collision is a non-blocking `warn`.
 */
export function evaluateItemDietary(
  item: { allergens?: string[] | null },
  profile: DietaryProfile,
): DietaryEvaluation {
  const avoids = profile.allergenAvoids;
  if (avoids.length === 0 || !item.allergens?.length) {
    return { status: 'clear', allergenHits: [] };
  }
  const hits: string[] = [];
  for (const itemAllergen of item.allergens) {
    // Resolve both sides to canonical keys first so 'dairy' ↔ 'Milk' and
    // 'shellfish' ↔ 'Crustaceans' collide; fall back to the loose token match
    // for values outside the canonical vocabulary (legacy free text).
    const itemKey = resolveAllergenKey(String(itemAllergen)) ?? undefined;
    if (
      avoids.some((avoid) => {
        if (itemKey) {
          const avoidKey = resolveAllergenKey(avoid);
          if (avoidKey) return itemKey === avoidKey;
        }
        return tokenMatches(String(itemAllergen), avoid);
      })
    ) {
      hits.push(String(itemAllergen));
    }
  }
  if (hits.length === 0) return { status: 'clear', allergenHits: [] };
  return { status: profile.strictness === 'block' ? 'blocked' : 'warn', allergenHits: hits };
}

export type ItemDietaryVerdict = {
  status: DietaryStatus;
  /** True when this dish is a reason to filter it out of a menu list. */
  clash: boolean;
  /** One short sentence for the row/card, or null when there is nothing to say. */
  note: string | null;
};

type VerdictItem = {
  allergens?: string[] | null;
  allergen_assessment?: 'declared' | 'none_present' | 'not_assessed' | null;
  dietary_tags?: string[] | null;
};

function dietaryTagLabel(value: string): string {
  const key = resolveDietaryTagKey(value) ?? value.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return DIETARY_TAG_ENTRIES.find((entry) => entry.key === key)?.label ?? value;
}

/**
 * What this dish means for this guest, in the Chow mobile shape
 * (`menuraq-chow-mobile` → lib/dietary-rules.ts).
 *
 * The app and the web must answer the same question with the same three
 * outcomes, because one is the product and the other is the QR entry point into
 * it:
 *
 *   clash   — it lists an allergen they avoid, or it is not marked with a diet
 *             they keep (when the merchant has declared any diet tags at all);
 *   unknown — the restaurant has not assessed allergens, which is NOT "none";
 *   fine    — nothing to say.
 *
 * Positive diet flags never raise a false alarm on an item with no tags: an
 * untagged dish is unknown, not proof that it is not vegan. That is the same
 * honesty rule `evaluateItemDietary` follows.
 */
export function describeItemDietary(
  item: VerdictItem,
  profile: DietaryProfile,
): ItemDietaryVerdict {
  if (isDietaryProfileEmpty(profile)) {
    return { status: 'clear', clash: false, note: null };
  }

  const evaluation = evaluateItemDietary(item, profile);
  if (evaluation.status !== 'clear') {
    const labels = canonicalAllergenLabels(evaluation.allergenHits);
    return {
      status: evaluation.status,
      clash: true,
      note: labels.length > 0 ? `Contains ${labels.join(', ').toLowerCase()}` : null,
    };
  }

  const tags = new Set(
    (item.dietary_tags ?? []).map((tag) => resolveDietaryTagKey(String(tag)) ?? String(tag).trim().toLowerCase()),
  );
  const missing = profile.dietaryFlags.filter((flag) => {
    const key = resolveDietaryTagKey(flag) ?? flag.trim().toLowerCase();
    return tags.size > 0 && !tags.has(key);
  });
  if (missing.length > 0) {
    return {
      status: 'warn',
      clash: true,
      note: `Not marked ${missing.map(dietaryTagLabel).join(', ').toLowerCase()}`,
    };
  }

  const assessed = item.allergen_assessment === 'declared' || item.allergen_assessment === 'none_present';
  if (profile.allergenAvoids.length > 0 && !item.allergens?.length && !assessed) {
    return { status: 'clear', clash: false, note: 'Allergens not listed: ask the restaurant' };
  }

  return { status: 'clear', clash: false, note: null };
}

/**
 * Subscribes to the device-local dietary profile, staying in sync across tabs
 * and across components in the same tab (via the custom update event).
 */
export function useDietaryProfile(): {
  profile: DietaryProfile;
  setProfile: (next: DietaryProfile) => void;
} {
  const [profile, setProfileState] = useState<DietaryProfile>(EMPTY_DIETARY_PROFILE);

  useEffect(() => {
    setProfileState(readDietaryProfile());
    const sync = () => setProfileState(readDietaryProfile());
    window.addEventListener(UPDATE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(UPDATE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setProfile = useCallback((next: DietaryProfile) => {
    writeDietaryProfile(next);
    setProfileState(sanitizeProfile(next));
  }, []);

  return { profile, setProfile };
}

/**
 * Doc 59 F1 , one dietary profile synced both ways through the customer session.
 *
 * The device-local profile stays the source of truth for the guard (guests
 * included, offline included). When a customer is signed in this hook adds the
 * two missing halves: on mount it restores the account profile into device-local
 * storage when this device has none (a fresh phone inherits "avoid peanuts" on
 * first paint), and every save pushes back to the account so the change follows
 * the customer across devices. A guest-set profile is never clobbered by the
 * restore , hydration only fills an empty device profile.
 */
/**
 * The account→device restore is a one-shot per page load.
 *
 * Module-level because `useDietaryProfileSync` has more than one consumer and the
 * work is not per-consumer. `...InFlight` collapses the simultaneous mounts (and
 * StrictMode's second mount) onto one request; `...Settled` stops the next consumer
 * from starting another once it has landed.
 */
let dietaryRestoreInFlight: Promise<void> | null = null;
let dietaryRestoreSettled = false;

export function useDietaryProfileSync(): {
  profile: DietaryProfile;
  setProfile: (next: DietaryProfile) => void;
  syncStatus: 'idle' | 'syncing' | 'synced' | 'error';
} {
  const { status: authStatus, customerFetch } = useCustomerSession();
  const { profile, setProfile } = useDietaryProfile();
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'error'>('idle');

  // Restore the account profile into this device once the session is known and
  // this device has none. Best-effort: if the request fails the device-local
  // profile still guards this device.
  //
  // The guard is ONCE PER PAGE LOAD, at module level, and that is the whole point.
  // This hook is mounted by more than one consumer (the ordering surface reads the
  // profile for the allergen guard; the preferences sheet edits it), and each
  // consumer's effect fires independently — so two consumers under StrictMode's
  // intentional double-invoke dispatched four identical `/preferences` reads before
  // the menu had finished painting. The profile is a fact about the account and this
  // device, not about a component, and the write is guarded below by
  // `isDietaryProfileEmpty` anyway, so repeats were pure waste on the critical path.
  useEffect(() => {
    if (authStatus !== 'authenticated') {
      // Signed out (or still resolving). Re-arm, so that signing back in on this same
      // page restores the NEW account's profile instead of inheriting the "already
      // restored" flag from whoever was signed in a moment ago. Without this the
      // one-shot would have been a one-shot for the whole document, and a
      // sign-out/sign-in without a reload would silently keep the old guard.
      dietaryRestoreSettled = false;
      return;
    }
    if (dietaryRestoreSettled || dietaryRestoreInFlight) return;
    let cancelled = false;

    dietaryRestoreInFlight = (async () => {
      try {
        const res = await customerFetch('/api/customer-auth/preferences');
        const data = (await res.json().catch(() => null)) as {
          ok?: boolean;
          dietary?: Partial<DietaryProfile> | null;
        } | null;
        if (cancelled || !data?.ok || !data.dietary) return;
        const remote = sanitizeProfile({ ...EMPTY_DIETARY_PROFILE, ...data.dietary });
        if (!isDietaryProfileEmpty(remote) && isDietaryProfileEmpty(readDietaryProfile())) {
          writeDietaryProfile(remote);
        }
      } catch {
        // Best-effort restore; the device-local profile still guards this device.
      } finally {
        dietaryRestoreSettled = true;
        dietaryRestoreInFlight = null;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authStatus, customerFetch]);

  const setProfileWithSync = useCallback(
    (next: DietaryProfile) => {
      // The guard must react instantly on this device regardless of network.
      setProfile(next);
      if (authStatus !== 'authenticated') {
        setSyncStatus('idle');
        return;
      }
      setSyncStatus('syncing');
      void (async () => {
        try {
          const res = await customerFetch('/api/customer-auth/preferences', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              dietary: {
                dietaryFlags: next.dietaryFlags,
                allergenAvoids: next.allergenAvoids,
                strictness: next.strictness,
              },
            }),
          });
          const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
          setSyncStatus(res.ok && data?.ok ? 'synced' : 'error');
        } catch {
          setSyncStatus('error');
        }
      })();
    },
    [authStatus, customerFetch, setProfile],
  );

  return { profile, setProfile: setProfileWithSync, syncStatus };
}
